import { Button as MuiButton, ButtonProps as MuiButtonProps, styled } from "@mui/material";

const StyledWarcButton = styled(MuiButton)(({ theme }) => {
  return {
    color: theme.palette.mode === "dark" ? theme.palette.common.white : theme.palette.text.primary,
    textTransform: "none",
    padding: "0",
    minWidth: "auto",
    width: "auto",
    backgroundColor: "transparent",
    borderRadius: "16px",
    minHeight: "40px",
    border: "none",
    "&:hover": {
      backgroundColor:
        theme.palette.mode === "light" ? "rgba(133, 137, 198, 0.08)" : "rgba(133, 137, 198, 0.25)",
    },
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    // Force pointer events to ensure tooltip works even when disabled
    pointerEvents: "auto",
    "&:disabled": {
      opacity: 0.7,
      cursor: "not-allowed",
    },
    "& .MuiButton-startIcon": {
      margin: 0,
    },
    "& .MuiButton-endIcon": {
      margin: 0,
    },
    "& .MuiTouchRipple-root": {
      display: "none",
    },
  };
});

interface WarcButtonProps extends MuiButtonProps {
  children: React.ReactNode;
  isPending?: boolean;
  isFeatureEnabled?: boolean;
}

/**
 * WarcButton - A specialized button for WARC (Web Archive) functionality
 *
 * This button is used to toggle WARC features and shows different states:
 * - Enabled: When feature flag is on and user has proper tier
 * - Disabled: When feature flag is off or user doesn't have proper tier
 * - Pending: During API calls or when waiting for responses
 *
 * The button automatically handles disabled states and cursor styling
 * based on the isFeatureEnabled and isPending props.
 */
export const WarcButton: React.FC<WarcButtonProps> = ({
  children,
  isPending = false,
  isFeatureEnabled = true,
  disabled,
  ...props
}) => {
  const isDisabled = disabled || isPending || !isFeatureEnabled;

  return (
    <StyledWarcButton
      size="small"
      variant="text"
      disabled={isDisabled}
      sx={{
        opacity: isPending ? 0.5 : 1,
        cursor: isDisabled ? "not-allowed" : "pointer",
      }}
      {...props}
    >
      {children}
    </StyledWarcButton>
  );
};

export default WarcButton;
